import React from 'react';
import { Control } from "./ControlRenderer";
import { Action } from "./ModalBox";
import labelEditor from '../../layouts/LabelEditor.json';
import textEditor from '../../layouts/TextEditor.json';
import dateEditor from '../../layouts/DateEditor.json';
import defaultEditor from '../../layouts/DefaultEditor.json';
import formEditor from '../../layouts/FormEditor.json';
import dataGridEditor from '../../layouts/DataGridEditor.json';
import flexEditor from '../../layouts/FlexEditor.json';
import flexEditorRule from '../../schema/FlexEditor.json';
import dropdownEditor from '../../layouts/DropdownEditor.json';
import buttonEditor from '../../layouts/ButtonEditor.json';
import uploadEditor from '../../layouts/UploadEditor.json';
import headerEditor from '../../layouts/HeaderEditor.json';
import gridEditor from '../../layouts/GridEditor.json';
import { DropdownControl } from "./controls/DropdownControl";
import { useFormData } from "@/components/FormDataProvider";
import ModalBox from './ModalBox';
import LabelControl from './controls/LabelControl';
import { FlexControl } from './controls/FlexControl';
import { GridControl } from './controls/GridControl';
import { ButtonControl } from './controls/ButtonControl';
import { DataGridControl } from './controls/DataGridControl';
import { UploadControl } from './controls/UploadControl';
import { firstElement, get } from './utils/Utils';
import { FormControl } from './controls/FormControl';
import { Rule } from './utils/Validation';
import { ValidationProvider } from '../ValidationProvider';

// Define ControlEditorProps interface
interface ControlEditorProps {
    controlToEdit: Control;
    data: any;
    dataKey: string | undefined;
    onComplete?: () => void; // Callback for completion
    onError?: () => void;     // Callback for error
    onClose?: () => void;     // Callback for error
}

export const getDefaultControl = (type: string): Control => {
    if (type === "label") {
        return {
            fontSize: 11,
            type: "label",
            label: "new label",
            name: "",
            horizontalAlignment: "right",
            verticalAlignment: "middle"
        } as LabelControl
    } else if (type === "text") {
        return {
            type: "text",
            label: "",
            name: "",
            horizontalAlignment: "stretch",
            verticalAlignment: "middle"
        } as Control
    } else if (type === "textarea") {
        return {
            type: "textarea",
            label: "",
            name: "",
            horizontalAlignment: "stretch",
            verticalAlignment: "middle"
        } as Control
    } else if (type === "select") {
        return {
            type: "select",
            label: "",
            name: "",
            horizontalAlignment: "stretch",
            verticalAlignment: "stretch"
        } as DropdownControl
    } else if (type === "date") {
        return {
            type: "date",
            label: "",
            name: "",
            horizontalAlignment: "stretch",
            verticalAlignment: "stretch"
        } as Control
    } else if (type === "check") {
        return {
            type: "check",
            label: "",
            name: "",
            horizontalAlignment: "center",
            verticalAlignment: "middle"
        } as Control
    } else if (type === "button") {
        return {
            type: "button",
            label: "Button",
            name: "",
            horizontalAlignment: "center",
            verticalAlignment: "middle"
        } as ButtonControl
    } else if (type === "gridbutton") {
        return {
            type: "gridbutton",
            label: "Button",
            name: "",
            horizontalAlignment: "center",
            verticalAlignment: "middle"
        } as ButtonControl
    } else if (type === "grid") {
        return {
            rows: [
                {}
            ],
            columns: [
                {}
            ],
            controls: [],
            type: "grid",
            label: "",
            name: "",
            horizontalAlignment: "",
            verticalAlignment: ""
        } as GridControl
    } else if (type === "flex") {
        return {
            controls: [],
            type: "flex",
            label: "",
            name: "",
            horizontalAlignment: "stretch",
            flexWrap: "wrap",
            colWidth: 300,
            gap: 4,
        } as FlexControl
    } else if (type === "form") {
        return {
            controls: [],
            type: "form",
            label: "",
            name: "NewForm",
            dataContextKey: "FORMDATA",
            version: 1.000,
            isReusable: true,
        } as FormControl
    } else if (type === "datagrid") {
        return {
            controls: [],
            type: "datagrid",
            label: "",
            name: "",
            horizontalAlignment: "",
            verticalAlignment: ""
        } as DataGridControl
    } else if (type === "tab") {
        return {
            controls: [],
            type: "tab",
            label: "",
            name: "",
            horizontalAlignment: "stretch",
            verticalAlignment: "stretch"
        } as Control
    } else if (type === "header") {
        return {
            controls: [],
            type: "header",
            label: "new header",
            name: ""
        } as Control
    } else if (type === "upload") {
        return {
            uploadAction: {},
            type: "upload",
            label: "",
            name: "",
            horizontalAlignment: "",
            verticalAlignment: ""
        } as UploadControl
    } else {
        throw new Error(`Unsupported control type: ${type}`);
    }
}

const ControlEditor: React.FC<ControlEditorProps> = ({ controlToEdit, data, dataKey, onComplete, onError, onClose }) => {
    const { formData } = useFormData();

    const getEditorAction = (): Action => {
        let editor;
        if (controlToEdit.type === "label") {
            editor = labelEditor;
        } else if (controlToEdit.type === "text") {
            editor = textEditor;
        } else if (controlToEdit.type === "date") {
            editor = dateEditor;
        } else if (controlToEdit.type === "form") {
            editor = formEditor;
        } else if (controlToEdit.type === "datagrid") {
            editor = dataGridEditor;
        } else if (controlToEdit.type === "grid") {
            editor = gridEditor;
        } else if (controlToEdit.type === "flex") {
            editor = flexEditor;
        } else if (controlToEdit.type === "button" || controlToEdit.type === "gridbutton" || controlToEdit.type === "link") {
            editor = buttonEditor;
        } else if (controlToEdit.type === "select") {
            editor = dropdownEditor;
            const itemsKeyControl = editor?.controls.find(item => item.dataKey === "itemsKey") as DropdownControl;
            if (itemsKeyControl && formData) {
                itemsKeyControl.items = Object.keys(formData).map(key => [key, key]);
            }
        } else if (controlToEdit.type === "upload") {
            editor = uploadEditor;
        } else if (controlToEdit.type === "header") {
            editor = headerEditor;
        } else {
            editor = defaultEditor;
        }

        const dataKeyControl = editor?.controls.find(item => item.dataKey === "dataKey") as DropdownControl;
        if (dataKeyControl) {
            if (controlToEdit.dataContextKey === "FORMDATA") {
                const itemsObject = get(formData, "FORMDATA", {});
                dataKeyControl.items = Object.keys(itemsObject)
                    .filter(key => Array.isArray(itemsObject[key]))
                    .map(key => [key, key]);
            } else if (controlToEdit.dataContextKey === "root") {
                dataKeyControl.items = Object.keys(formData ?? {}).map(key => [key, key]);
            } else {
                dataKeyControl.items = dataKey ? get(formData, `${dataKey}_Columns`, Object.keys(firstElement(data)).map(key => [key, key])) : Object.keys(firstElement(data)).map(key => [key, key]);
            }
        }

        const postDataKeyControl = editor?.controls.find(item => item.dataKey === "postDataKey") as DropdownControl;
        if (postDataKeyControl) {
            if (controlToEdit.dataContextKey === "FORMDATA") {
                const itemsObject = get(formData, "FORMDATA");
                postDataKeyControl.items = Object.keys(itemsObject)
                    .filter(key => Array.isArray(itemsObject[key]))
                    .map(key => [key, key]);
            } else if (controlToEdit.dataContextKey === "root") {
                dataKeyControl.items = Object.keys(formData ?? {}).map(key => [key, key]);
            } else {
                postDataKeyControl.items = dataKey ? get(formData, `${dataKey}_Columns`, Object.keys(firstElement(data)).map(key => [key, key])) : Object.keys(firstElement(data)).map(key => [key, key]);
            }
        }

        return {
            type: 'messagebox',
            payload: {
                title: `Edit ${controlToEdit.type} properties`,
                layout: editor,
                okbutton: true
            }
        };
    };

    const getValidationRule = (): Rule | null => {
        let rule = null;
        if (controlToEdit.type === "flex") {
            rule = flexEditorRule;
        }
        return rule;
    }

    const action = getEditorAction();
    const rule = getValidationRule();

    const handleComplete = () => {
        if (controlToEdit.type === "select") {
            // need to check if datakey or items are specified
            const dropdownControl = controlToEdit as DropdownControl;
            if (dropdownControl.itemsKey && dropdownControl.items && dropdownControl.items.length == 0) {
                delete dropdownControl.items;
            }
        } else if (controlToEdit.type === "button" || controlToEdit.type === "gridbutton" || controlToEdit.type === "link") {
            const buttonControl = controlToEdit as ButtonControl;
            if (buttonControl.clickAction.type == "messagebox") {
                if (buttonControl.clickAction.payload.message) {
                    buttonControl.clickAction.payload.layout = {};
                }
                else {
                    buttonControl.clickAction.payload.layout = getDefaultControl("grid");
                }
            }
        }
        if (onComplete) onComplete();
    };

    const handleError = () => {
        if (onError) onError();
    };

    const handleClose = () => {
        if (onClose) onClose();
    };

    return (
        <>
            {rule ? (
                <ValidationProvider rule={rule}>
                    <ModalBox
                        action={action}
                        data={controlToEdit}
                        onDataChange={() => { }}
                        mode="edit"
                        onComplete={handleComplete}
                        onError={handleError}
                        onClose={handleClose}
                        sx={{ minWidth: '300px' }}
                    />
                </ValidationProvider>
            ) : (
                <ModalBox
                    action={action}
                    data={controlToEdit}
                    onDataChange={() => { }}
                    mode="edit"
                    onComplete={handleComplete}
                    onError={handleError}
                    onClose={handleClose}
                    sx={{ minWidth: '300px' }}
                />
            )}
        </>
    );
};

export default ControlEditor;